import {HttpParams} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {FlamingoHttpService} from '@flamingo/service/flamingo-http.service';
import {NEVER, Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {AccountUpdate} from '../../../../core/model/account-update.model';
import {AuctionView, CompanyDocument} from '../../../../core/model/auction-view.model';
import {BidMassiveRequest, BidMassiveResult} from '../../../../core/model/bid-massive.model';
import {BuyerAccountView} from '../../../../core/model/buyer-account-view.model';
import {BuyerFundView} from '../../../../core/model/buyer-fund-view.model';
import {PaginatedList} from '../../../../core/model/paginated-list.model';
import {UserType} from '../../../../core/model/user.model';
import {VirtualBalance} from '../../../../core/model/virtual-balance.model';
import {WalletDetail} from '../../../../core/model/wallet-detail.model';
import {WalletP2PTransaction} from '../../../../core/model/wallet-P2P-transaction.model';
import {MainService} from '../../main.service';

@Injectable({
  providedIn: 'root',
})
export class BuyerService {
  private _fundId?: number;

  get fundId(): number | null {
    if (this._fundId) {
      return this._fundId;
    }

    return this.user.funds[0] ? this.user.funds[0].id : null;
  }

  set fundId(value: number) {
    this._fundId = value;
  }

  get user(): BuyerAccountView {
    return this._mainService.user as BuyerAccountView;
  }

  set user(value: BuyerAccountView) {
    this._mainService.user = value;
  }

  get userSelectedFund(): BuyerFundView {
    return this.userFund(this.fundId);
  }

  constructor(
    private _httpService: FlamingoHttpService,
    private _mainService: MainService,
  ) {
  }

  userFund(fundId?: number): BuyerFundView {
    return this.user.funds.find(f => f.id === fundId);
  }

  public getAccount(): Observable<BuyerAccountView> {
    return this._mainService.getAccount() as Observable<BuyerAccountView>;
  }

  public updateAccount(account: AccountUpdate): Observable<BuyerAccountView> {
    return this._mainService.updateAccount(account) as Observable<BuyerAccountView>;
  }

  // API

  public auctionInProgressList(
    page?: number,
    pageSize?: number,
    sort?: string,
    sortOrder?: string,
    filter?: string,
  ): Observable<PaginatedList<AuctionView>> {
    const params = new HttpParams()
      .set('page', page ? `${page}` : '0')
      .set('pageSize', pageSize ? `${pageSize}` : '10')
      .set('sort', sort ? sort : '')
      .set('sortOrder', sortOrder ? sortOrder : 'ASC')
      .set('filter', filter ? filter : '');

    return this._httpService.get(`/auction-view/in-progress`, params);
  }

  public auctionAwardedList(
    params: HttpParams,
    type?: 'ALL' | 'DOWNPAYMENT' | 'SETTLEMENT',
  ): Observable<PaginatedList<AuctionView>> {

    let url: string;
    switch (type) {
      case 'DOWNPAYMENT':
        url = '/auction-view/awarded-pending-downpayment';
        break;
      case 'SETTLEMENT':
        url = '/auction-view/awarded-pending-settlement';
        break;
      case 'ALL':
      default:
        url = '/auction-view/awarded';
        break;
    }

    return this._httpService.get(url, params);
  }

  public getSellerDocuments(auctionId: number): Observable<CompanyDocument> {
    return this._httpService.get(`/auction/${auctionId}/seller-documents`);
  }

  public getDebtorDocuments(auctionId: number): Observable<CompanyDocument> {
    return this._httpService.get(`/auction/${auctionId}/debtor-documents`);
  }

  public downloadInvoice(auctionId: number): Observable<any> {
    return this._httpService.getUrl(`/auction/${auctionId}/download-invoice`);
  }

  public downloadRating(auctionId: number, userType: UserType): Observable<any> {
    return this._httpService.getUrl(`/auction/${auctionId}/${userType.toLowerCase()}/download-rating`);
  }

  public downloadAttachment(auctionId: number, awsKey: string): Observable<any> {
    return this._httpService.getUrl(`/auction/${auctionId}/download-attachment/${awsKey}`);
  }

  public sendBid(
    auctionId: number,
    bidInterestRate: number,
    fundId: number,
    transactionPassword?: string,
  ): Observable<any> {
    const body = { bidInterestRate, transactionPassword, fundId };

    return this._httpService.post(`/auction/${auctionId}/bid`, body);
  }

  /**
   * Place a bid on several auctions at once, with the same fund and transaction password.
   * The response holds one outcome per requested auction, in the same order: a failure on one
   * auction does not prevent the others from being placed.
   */
  public sendMassiveBid(request: BidMassiveRequest): Observable<Array<BidMassiveResult>> {
    return this._httpService.post(`/auction/bid/massive`, request);
  }

  public downloadAuctionConditions(
    auctionId: number,
    bidInterestRate: number,
    fundId: number,
  ): Observable<any> {
    const params = new HttpParams()
      .set('bidInterestRate', '' + bidInterestRate)
      .set('fundId', '' + fundId);
    return this._httpService.download(`/auction/${auctionId}/conditions`, params).pipe(
      map(res => {
        let filename = 'file';
        const disposition = res.headers.get('content-disposition');
        if (disposition && disposition.indexOf('attachment') !== -1) {
          const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
          const matches = filenameRegex.exec(disposition);
          if (matches != null && matches[1]) {
            filename = matches[1].replace(/['"]/g, '');
          }
        }

        const obj = window.URL.createObjectURL(new Blob([res.body], { type: res.body.type }));
        const link = window.document.createElement('a');
        link.download = filename;
        link.href = obj;
        link.click();
      }),
    );
  }

  /**
   * Get details of the fund wallet passed as parameter.
   * If no parameter is passed, uses the current user fund wallet.
   * @param fundId The Id of the wallet to retrieve
   */
  public getFundWallet(fundId?: number): Observable<WalletDetail> {
    if (!fundId && !this.fundId) {
      return NEVER;
    }
    return this._httpService.get(`/account/fund/${fundId ? fundId : this.fundId}`);
  }

  /**
   * Get virtual balance for the fund (real balance minus committed downpayments).
   * Used in buyer app to show available liquidity for bidding.
   * @param fundId The fund id
   */
  public getVirtualBalance(fundId?: number): Observable<VirtualBalance> {
    if (!fundId && !this.fundId) {
      return NEVER;
    }
    return this._httpService.get(`/account/fund/${fundId ?? this.fundId}/virtual-balance`);
  }

  /**
   * Get transactions detail of the current user fund wallet
   */
  public getFundWalletTransactions(): Observable<Array<WalletP2PTransaction>> {
    if (!this.fundId) {
      return NEVER;
    }
    return this._httpService.get(`/account/fund/${this.fundId}/transactions`);
  }

  /**
   * @param fundId when omitted the report covers every fund of the buyer
   */
  public exportAuctions(fundId?: number): Observable<any> {
    const params = fundId ? new HttpParams().set('fundId', `${fundId}`) : undefined;

    return this._httpService.download(`/auction/awarded/export`, params).pipe(map(r => r.body));
  }

  public exportAuctionsInProgress(): Observable<any> {
    return this._httpService.download(`/auction/in-progress/export`).pipe(map(r => r.body));
  }

  public exportAuctionsScheduled(): Observable<any> {
    return this._httpService.download(`/auction/scheduled/export`).pipe(map(r => r.body));
  }

  // ref new-cashme/cashme-issues#1783 - quota defaults to 'ALL': both Principal and Interest wallets
  // (when the fund has one) get exported in a single file, mirroring the CRM's "Entrambi i wallet" export.
  public exportWalletTransactions(fundId: number, type: 'csv' | 'xls', quota: FundWalletExportQuota = 'ALL'): Observable<any> {
    const params = new HttpParams().set('wallet', quota);

    return this._httpService
      .download(`/account/fund/${fundId}/transactions/export/${type}`, params)
      .pipe(map(r => r.body));
  }
}

/** ref new-cashme/cashme-issues#1783 - mirrors the backend's FundWalletQuota. */
export type FundWalletExportQuota = 'PRINCIPAL' | 'INTEREST' | 'ALL';
